import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../../store";
import { ApplicationError } from "../../../models/errors/application-error";
import ProductService from "../../../services/product/product-service";
import { ApplicationProductList } from "../../../models/product/application-product-list";
import { ApplicationSubDepartmentList } from "../../../models/organisation/application-sub-department-list";
import { ApplicationSubDepartment } from "../../../models/organisation/application-sub-department";
import DepartmentService from "../../../services/organisation/department-service";
import { ApplicationProduct } from "../../../models/product/application-product";

export const fetchSubDepartments = createAsyncThunk<ApplicationSubDepartmentList | null, number, { state: RootState }>(
    'continousproducts/department/subDepartments/fetch',
    async (departmentId, { getState, rejectWithValue }) => {
        const departmentService = new DepartmentService();
        const state = getState().continousProduct;
        try {
            var currentPosition: number = state.subDepartments?.currentPosition ?? 0;
            var fetchNext: number = 15;
            return await departmentService.getSubDepartments(departmentId, currentPosition, fetchNext, true);
        } catch (error: any) {
            const apiError = ApplicationError.handleApiError(error, {});
            return rejectWithValue(apiError);
        }
    }
);


export const fetchProducts = createAsyncThunk<ApplicationProductList | null, number, { state: RootState }>(
    'continousproducts/products/fetch',
    async (subDepartmentId, { getState, rejectWithValue }) => {
        const state = getState();
        const productService = new ProductService();
        try {
            var currentPosition: number = state.continousProduct.products?.currentPosition ?? 0;
            var fetchNext: number = 10;
            var count: number = state.continousProduct.products?.count ?? -1;

            if (currentPosition != count) {
                return await productService.getProductsBySubDepartments(state.branch.selectedBranch!.id, subDepartmentId, currentPosition, fetchNext, false);
            }

            return null;
        } catch (error: any) {
            const apiError = ApplicationError.handleApiError(error, {});
            return rejectWithValue(apiError);
        }
    }
);

export const updateBranchProduct = createAsyncThunk<{ productId: number, active: boolean }, { productId: number, active: boolean }, { state: RootState }>(
    'continousproducts/branchProduct/update',
    async ({ productId, active }, { getState, rejectWithValue }) => {
        const state = getState();
        const productService = new ProductService();
        try {
            await productService.upsertBranchProduct(state.branch.selectedBranch!.id, productId, active)
            return { productId, active };
        } catch (error: any) {
            const apiError = ApplicationError.handleApiError(error, {});
            return rejectWithValue(apiError);
        }
    }
);



interface ContinousProductState {
    subDepartments: ApplicationSubDepartmentList | null,
    selectedSubDepartment: ApplicationSubDepartment | null,
    completedSubDepartments: ApplicationSubDepartment[]
    products: ApplicationProductList | null,
    currentProduct: ApplicationProduct | null
}


const initialState: ContinousProductState = {
    subDepartments: null,
    selectedSubDepartment: null,
    products: null,
    currentProduct: null,
    completedSubDepartments: [],
};

const continiousProductSlice = createSlice({
    name: 'continousproducts',
    initialState,
    reducers: {
        setSelectedSubDepartment: (state, action: PayloadAction<ApplicationSubDepartment | null>) => {
            state.selectedSubDepartment = action.payload;
        },
        resetSelectedSubDepartment: (state) => {
            state.selectedSubDepartment = initialState.selectedSubDepartment;
        },
        setCurrentProduct: (state, action: PayloadAction<ApplicationProduct | null>) => {
            state.currentProduct = action.payload;
        },
        setCompletedSubDepartments: (state, action: PayloadAction<ApplicationSubDepartment | null>) => {
            if (action.payload && state.completedSubDepartments.indexOf(action.payload) == -1) {
                state.completedSubDepartments.push(action.payload);
            }
        },
        resetCompletedSubDepartments: (state) => {
            state.completedSubDepartments = []
        },
        resetProducts: (state) => {
            state.products = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchSubDepartments.fulfilled, handleSubDepartmentFulfilled)
            .addCase(fetchProducts.fulfilled, handleFetchProductsFulfilled)
            .addCase(updateBranchProduct.fulfilled, handleUpdateBranchProduct)
    },
});


const handleUpdateBranchProduct = (
    state: ContinousProductState,
    action: PayloadAction<{ productId: number, active: boolean }>
) => {
    if (!action.payload.active) {
        const productIndex = state.products?.products.findIndex(x => x.id === action.payload.productId);
        if (productIndex && productIndex !== -1) {
            state.products?.products.splice(productIndex, 1);
            if (state.products?.count) {
                state.products.count = state.products.count - 1;
            }
        }
    }
}

const handleSubDepartmentFulfilled = (
    state: ContinousProductState,
    action: PayloadAction<ApplicationSubDepartmentList | null>
) => {

    state.subDepartments = action.payload;
}


const handleFetchProductsFulfilled = (
    state: ContinousProductState,
    action: PayloadAction<ApplicationProductList | null>
) => {
    if (action.payload) {
        const { products, currentPosition, count } = action.payload;
        if (state.products) {
            state.products.products = state.products.products.concat(products);
            state.products.currentPosition = currentPosition;
            state.products.count = count;
        } else {
            state.products = action.payload;
            state.currentProduct = action.payload.products[0] ?? null;
        }
    }
};



export const {
    setCurrentProduct,
    setSelectedSubDepartment,
    resetProducts,
    setCompletedSubDepartments,
    resetCompletedSubDepartments,
    resetSelectedSubDepartment
} = continiousProductSlice.actions;

export default continiousProductSlice.reducer;
